import { useEffect, useLayoutEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

import { fetchPlaybookCards, fetchAssets } from "../lib/api";

gsap.registerPlugin(ScrollTrigger);

const ACTIVICA_PIXEL =
    '"FT Activica Pixel", "FT Activica Normal", monospace';
const ACTIVICA =
    '"FT Activica Normal", "FT Activica Strong", sans-serif';
const FIGTREE = '"Figtree", "Inter", system-ui, sans-serif';
const GREEN_GRADIENT =
    "linear-gradient(180deg, #90FDA6 -0.02%, #F8FF28 132.41%)";

const FALLBACK_CARDS = [
    { id: "s0", slot_index: 0, number: "01", label: "Hackathons", image_url: null },
    { id: "s1", slot_index: 1, number: "02", label: "Mixers", image_url: null },
    { id: "s2", slot_index: 2, number: "03", label: "Vibecoding for Businesses", image_url: null },
    { id: "s3", slot_index: 3, number: "04", label: "Demo Nights", image_url: null },
];

// New design (per spec): image fills the right half of the card as a rounded
// rectangle. ~47% of the card width, slightly inset top/bottom, with a 16px
// inner radius so its edges sit just inside the parent card's 20px radius.
const IMAGE_RADIUS = 16;

export default function Expectations() {
    const isMobile =
        typeof window !== "undefined" && window.innerWidth < 768;
    if (isMobile) {
        return <MobileExpectations />;
    }
    return <DesktopExpectations />;
}

function DesktopExpectations() {
    const sectionRef = useRef(null);
    const stageRef = useRef(null);
    const progressRef = useRef(null);
    const [cards, setCards] = useState(FALLBACK_CARDS);

    // Pull dynamic card content (images / labels / numbers) from the API.
    useEffect(() => {
        let cancelled = false;
        fetchPlaybookCards()
            .then((data) => {
                if (cancelled || !Array.isArray(data) || data.length === 0) return;
                setCards(data);
            })
            .catch(() => {
                /* keep fallback */
            });
        return () => {
            cancelled = true;
        };
    }, []);

    useLayoutEffect(() => {
        const section = sectionRef.current;
        const stage = stageRef.current;
        if (!section || !stage) return;

        const ctx = gsap.context(() => {
            const cardEls = gsap.utils.toArray(".playbook-card");
            if (cardEls.length === 0) return;
            const transitions = Math.max(1, cardEls.length - 1);

            cardEls.forEach((card, i) => {
                if (i === 0) {
                    gsap.set(card, { yPercent: 0, skewY: 0, scale: 1, opacity: 1 });
                } else {
                    gsap.set(card, {
                        yPercent: 110,
                        skewY: -1.5,
                        scale: 1,
                        opacity: 1,
                    });
                }
            });

            const tl = gsap.timeline({
                defaults: { ease: "none" },
                scrollTrigger: {
                    trigger: section,
                    start: "top top",
                    end: () => `+=${transitions * window.innerHeight}`,
                    scrub: 0.5,
                    pin: stage,
                    pinSpacing: true,
                    anticipatePin: 1,
                    invalidateOnRefresh: true,
                    onUpdate: (self) => {
                        if (progressRef.current) {
                            progressRef.current.style.transform = `scaleX(${self.progress})`;
                        }
                    },
                },
            });

            for (let i = 1; i < cardEls.length; i++) {
                tl.to(
                    cardEls[i],
                    { yPercent: 0, skewY: 0, duration: 1 },
                    i - 1,
                );
                tl.to(
                    cardEls[i - 1],
                    { scale: 0.88, opacity: 0.9, duration: 1 },
                    i - 1,
                );
            }
        }, section);

        return () => ctx.revert();
        // We intentionally run setup once. The fallback already has the
        // correct slot count, and API data just hydrates label/image text
        // into the existing DOM — no GSAP re-init needed.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totalCards = cards.length + 1; // +1 for the title card
    return (
        <section
            id="expectations"
            data-testid="expectations-section"
            ref={sectionRef}
            className="relative bg-background border-t border-line"
        >
            <div
                ref={stageRef}
                data-testid="playbook-stage"
                className="relative w-full overflow-hidden"
                style={{ height: "100vh", background: "#0a0a0a" }}
            >
                {/* Progress bar */}
                <div
                    aria-hidden
                    className="absolute top-0 left-0 right-0 z-50"
                    style={{
                        height: 4,
                        background: "rgba(255,255,255,0.04)",
                    }}
                >
                    <div
                        ref={progressRef}
                        data-testid="playbook-progress"
                        style={{
                            height: "100%",
                            background: GREEN_GRADIENT,
                            transformOrigin: "left center",
                            transform: "scaleX(0)",
                            willChange: "transform",
                        }}
                    />
                </div>

                {/* Card 0: title — same rounded frame to match the stat cards */}
                <CardFrame index={0} total={totalCards}>
                    <TitleCardContent />
                </CardFrame>

                {cards.map((c, i) => (
                    <CardFrame key={c.slot_index} index={i + 1} total={totalCards}>
                        <StatCardContent
                            number={c.number}
                            label={c.label}
                            imageUrl={c.image_url}
                            slotIndex={c.slot_index}
                            order={i + 1}
                            total={cards.length}
                        />
                    </CardFrame>
                ))}
            </div>
        </section>
    );
}

/* Shared centered rounded-card frame. The frame is what GSAP animates;
 * the inner content is the actual stat / title art.
 */
function CardFrame({ index, total, children }) {
    return (
        <div
            data-testid={`playbook-card-${index}`}
            className="playbook-card absolute inset-0 will-change-transform flex items-center justify-center px-4 sm:px-8 md:px-14 lg:px-20"
            style={{ zIndex: 10 + index, transformOrigin: "50% 0%" }}
            aria-hidden={index === 0 ? undefined : "false"}
            data-card-index={index}
            data-card-total={total}
        >
            <div
                className="relative w-full overflow-hidden"
                style={{
                    maxWidth: "min(1348px, 96vw)",
                    aspectRatio: "1348 / 836",
                    maxHeight: "calc(100vh - 96px)",
                    background: "#1F1F1F",
                    borderRadius: 20,
                    boxShadow: "inset 0 8px 9.7px rgba(169,169,169,0.25)",
                }}
            >
                {children}
            </div>
        </div>
    );
}

function TitleCardContent() {
    // The SVG has the card background + "THE VIBECITY / PLAYBOOK" type baked
    // in, so we just fill the card frame edge-to-edge. No wrapper, no
    // subtitle here - the subtitle lives below the pinned stack instead.
    return (
        <img
            src="https://customer-assets.emergentagent.com/job_vibecity-lead/artifacts/i7k6exuj_heroplaybook.svg"
            alt="The Vibecity Playbook"
            data-testid="playbook-title"
            className="absolute inset-0 w-full h-full"
            style={{ objectFit: "cover", borderRadius: 20 }}
        />
    );
}

function StatCardContent({ number, label, imageUrl, slotIndex }) {
    const src = imageUrl
        ? imageUrl.startsWith("/")
            ? `${window.location.origin}${imageUrl}`
            : imageUrl
        : null;
    const hasNumber = !!(number && String(number).trim());
    return (
        <>
            {/* Left half: optional number pill + event name title */}
            <div
                className="absolute inset-y-0 left-0 flex flex-col justify-between px-[6%] py-[6%]"
                style={{ width: "50%" }}
            >
                {hasNumber ? (
                    <div
                        data-testid={`playbook-number-${slotIndex}`}
                        className="inline-flex items-center self-start"
                        style={{
                            fontFamily: ACTIVICA_PIXEL,
                            fontWeight: 400,
                            color: "#F7F4DA",
                            fontSize: "clamp(14px, 1.2vw, 20px)",
                            letterSpacing: "0.18em",
                            padding: "8px 16px",
                            border: "1px solid rgba(247,244,218,0.25)",
                            borderRadius: 999,
                            whiteSpace: "nowrap",
                            textTransform: "uppercase",
                        }}
                    >
                        {number}
                    </div>
                ) : (
                    <span aria-hidden />
                )}
                <h3
                    data-testid={`playbook-label-${slotIndex}`}
                    style={{
                        fontFamily: FIGTREE,
                        fontWeight: 900,
                        fontSize: "clamp(2rem, 4.4vw, 5rem)",
                        lineHeight: 1.02,
                        color: "#FFFCE1",
                        letterSpacing: "-0.015em",
                        margin: 0,
                    }}
                >
                    {label}
                </h3>
            </div>

            {/* Right half: rounded-rectangle image, ~47% wide, inset 4% top/bottom */}
            <div
                className="absolute"
                style={{
                    right: "1.7%",
                    top: "4%",
                    bottom: "4%",
                    width: "47%",
                    borderRadius: IMAGE_RADIUS,
                    overflow: "hidden",
                    background: src ? "#0d0d0d" : "rgba(255,255,255,0.04)",
                    boxShadow: "inset 0 0 0 1px rgba(255,255,255,0.05)",
                }}
                data-testid={`playbook-blob-${slotIndex}`}
            >
                {src ? (
                    <img
                        src={src}
                        alt=""
                        className="w-full h-full object-cover"
                        loading="lazy"
                    />
                ) : (
                    <div
                        className="w-full h-full grid place-items-center"
                        style={{
                            fontFamily: FIGTREE,
                            fontWeight: 600,
                            fontSize: "0.7rem",
                            letterSpacing: "0.3em",
                            color: "rgba(255,255,255,0.35)",
                            textTransform: "uppercase",
                        }}
                    >
                        Image — Slot {String(slotIndex + 1).padStart(2, "0")}
                    </div>
                )}
            </div>
        </>
    );
}

/* ===== Mobile Vibecity Playbook ===== */

function MobileExpectations() {
    const sectionRef = useRef(null);
    const stageRef = useRef(null);
    const [cards, setCards] = useState(FALLBACK_CARDS);
    const [imgMap, setImgMap] = useState({});

    useEffect(() => {
        let cancelled = false;
        Promise.all([
            fetchPlaybookCards().catch(() => null),
            fetchAssets().catch(() => null),
        ]).then(([pc, assets]) => {
            if (cancelled) return;
            if (Array.isArray(pc) && pc.length > 0) {
                setCards(pc);
            }
            if (Array.isArray(assets)) {
                const m = {};
                for (const a of assets) {
                    if (a.key?.startsWith("playbook-mobile-") && a.image_url) {
                        m[a.key] = a.image_url;
                    }
                }
                setImgMap(m);
            }
        });
        return () => {
            cancelled = true;
        };
    }, []);

    useLayoutEffect(() => {
        const section = sectionRef.current;
        const stage = stageRef.current;
        if (!section || !stage) return;

        const ctx = gsap.context(() => {
            const cardEls = gsap.utils.toArray(".m-playbook-card");
            if (cardEls.length === 0) return;
            const transitions = Math.max(1, cardEls.length - 1);

            cardEls.forEach((card, i) => {
                if (i === 0) {
                    gsap.set(card, { yPercent: 0, scale: 1, opacity: 1 });
                } else {
                    gsap.set(card, { yPercent: 110, scale: 1, opacity: 1 });
                }
            });

            const tl = gsap.timeline({
                defaults: { ease: "none" },
                scrollTrigger: {
                    trigger: section,
                    start: "top top",
                    end: () => `+=${transitions * window.innerHeight}`,
                    scrub: 0.5,
                    pin: stage,
                    pinSpacing: true,
                    anticipatePin: 1,
                    invalidateOnRefresh: true,
                },
            });

            for (let i = 1; i < cardEls.length; i += 1) {
                tl.to(cardEls[i], { yPercent: 0, duration: 1 }, i - 1);
                tl.to(
                    cardEls[i - 1],
                    { scale: 0.92, opacity: 0.85, duration: 1 },
                    i - 1,
                );
            }
        }, section);
        return () => ctx.revert();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const totalCards = cards.length + 1; // +1 for the title slide

    return (
        <section
            id="expectations"
            data-testid="expectations-section"
            ref={sectionRef}
            className="relative bg-background"
        >
            <div
                ref={stageRef}
                data-testid="playbook-mobile-stage"
                className="relative w-full overflow-hidden"
                style={{ height: "100svh", background: "#0E100F" }}
            >
                {/* Title card */}
                <div
                    data-testid="playbook-mobile-title"
                    className="m-playbook-card absolute inset-0 w-full h-full will-change-transform"
                    style={{ zIndex: 10, transformOrigin: "50% 0%" }}
                >
                    <div
                        className="w-full h-full flex items-center justify-center"
                        style={{ background: "#0E100F", padding: 16 }}
                    >
                        <img
                            src="/assets/playbook-hero-mobile.svg"
                            alt="The Vibecity Playbook"
                            className="block"
                            style={{
                                width: "min(96vw, 460px)",
                                height: "auto",
                                aspectRatio: "327 / 369",
                                borderRadius: 24,
                            }}
                        />
                    </div>
                </div>

                {/* Stat cards */}
                {cards.map((c, i) => {
                    const key = `playbook-mobile-${i + 1}`;
                    const imageUrl = imgMap[key] || c.image_url;
                    return (
                        <MobilePlaybookCard
                            key={c.slot_index ?? i}
                            zIndex={11 + i}
                            order={i + 1}
                            total={totalCards - 1}
                            number={c.number}
                            label={c.label}
                            imageUrl={imageUrl}
                            slotIndex={c.slot_index ?? i}
                        />
                    );
                })}
            </div>
        </section>
    );
}

function MobilePlaybookCard({ zIndex, order, total, number, label, imageUrl, slotIndex }) {
    const src = imageUrl
        ? imageUrl.startsWith("/")
            ? `${window.location.origin}${imageUrl}`
            : imageUrl
        : null;
    return (
        <div
            data-testid={`playbook-mobile-card-${order}`}
            className="m-playbook-card absolute inset-0 w-full h-full will-change-transform"
            style={{
                zIndex,
                transformOrigin: "50% 0%",
                background: "transparent",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                padding: 16,
            }}
        >
            {/* Card matches the playbook hero SVG format: 327×369 portrait,
             *  matching the title slide exactly. */}
            <div
                className="relative overflow-hidden"
                style={{
                    width: "min(96vw, 460px)",
                    aspectRatio: "327 / 369",
                    background: "#1F1F1F",
                    borderRadius: 24,
                    boxShadow: "inset 0 8px 9.7px rgba(169,169,169,0.25)",
                }}
            >
                {/* Top: rectangular image filling top ~49% of card */}
                <div
                    className="absolute left-0 right-0 top-0"
                    style={{
                        height: "48.6%",
                        margin: "0 7px",
                        background: src
                            ? "#0d0d0d"
                            : "rgba(255,255,255,0.04)",
                        overflow: "hidden",
                    }}
                >
                    {src ? (
                        <img
                            src={src}
                            alt=""
                            className="w-full h-full"
                            loading="lazy"
                            style={{ objectFit: "cover" }}
                        />
                    ) : (
                        <div
                            className="w-full h-full grid place-items-center"
                            style={{
                                fontFamily: FIGTREE,
                                fontWeight: 600,
                                fontSize: 11,
                                letterSpacing: "0.3em",
                                color: "rgba(255,255,255,0.45)",
                                textTransform: "uppercase",
                            }}
                        >
                            Image — Slot{" "}
                            {String(slotIndex + 1).padStart(2, "0")}
                        </div>
                    )}
                </div>

                {/* Eyebrow index, sits below image */}
                <div
                    className="absolute"
                    style={{
                        left: 20,
                        top: "52%",
                        fontFamily: FIGTREE,
                        fontWeight: 600,
                        fontSize: 11,
                        letterSpacing: "0.3em",
                        color: "#646464",
                        textTransform: "uppercase",
                    }}
                    data-testid={`playbook-mobile-eyebrow-${slotIndex}`}
                >
                    {String(order).padStart(2, "0")} /{" "}
                    {String(total).padStart(2, "0")}
                </div>

                {/* Giant grey numeral */}
                <div
                    data-testid={`playbook-mobile-number-${slotIndex}`}
                    className="absolute"
                    style={{
                        left: 16,
                        top: "58%",
                        fontFamily: ACTIVICA,
                        fontWeight: 300,
                        color: "#575757",
                        fontSize: "min(38vw, 150px)",
                        lineHeight: 0.9,
                        letterSpacing: "-0.04em",
                        userSelect: "none",
                        pointerEvents: "none",
                    }}
                >
                    {number}
                </div>

                {/* Label, bottom-right */}
                <div
                    data-testid={`playbook-mobile-label-${slotIndex}`}
                    className="absolute"
                    style={{
                        right: 20,
                        bottom: 22,
                        maxWidth: "70%",
                        fontFamily: FIGTREE,
                        fontWeight: 800,
                        fontSize: 22,
                        lineHeight: 1.12,
                        color: "#FFFCE1",
                        letterSpacing: "-0.005em",
                        textAlign: "right",
                    }}
                >
                    {label}
                </div>
            </div>
        </div>
    );
}

